import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  ActivitiesDataScope,
  DefaultActivitiesAndNotificationsFilters,
  getStoreScope,
  TrackingActivitiesActions,
  TrackingActivitiesSelectors,
} from '@core/activities-and-notifications-data';
import { ActivitiesListComponent } from '@core/activities-and-notifications-ui';
import type { TrackingActivity } from '@core/activities-types';
import { filterBoolean } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  EmptyStateComponent,
  ScrollableDirective,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { ActivityPageDataService } from '../../../data/single-requisition/activity-page-data.service';
import { ActivityFacade } from '../../../facades/single-requisition/activity.facade';

@Component({
  selector: 'rt-requisitions-activities-container',
  templateUrl: './requisitions-activities-container.component.html',
  styleUrls: ['./requisitions-activities-container.component.less'],
  imports: [
    EmptyStateComponent,
    InfiniteScrollModule,
    SpinnerComponent,
    ActivitiesListComponent,
    TranslatePipe,
  ],
})
export class RequisitionsActivitiesContainerComponent {
  private readonly data = inject(ActivityPageDataService);
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly activityFacade = inject(ActivityFacade);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly hasWorkflowAutomations = injectDevFlag(DevFlag.WorkflowAutomations);

  public readonly scrollable = inject(ScrollableDirective);

  public isLoaded: boolean;
  public activities: TrackingActivity[] = [];
  public canManageCandidate: boolean;
  public isLoading = toSignal(this.data.isLoading$, { initialValue: false });

  private requisitionId: number;

  private cursorPosition: number | null = null;
  private hasMore = true;

  public ngOnInit() {
    combineLatest([
      this.data.activities$,
      this.data.manageCandidateAbility$,
      this.data.requisition$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([activities, canManageCandidate, requisition]) => {
        this.activities = activities;
        this.canManageCandidate = canManageCandidate;
        this.requisitionId = requisition.id;
        this.cdRef.detectChanges();
      });

    this.data.isLoaded$.pipe(filterBoolean, takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isLoaded = true;
      this.cdRef.detectChanges();
    });

    this.data.requisition$
      .pipe(
        switchMap(requisition =>
          this.store.select(
            TrackingActivitiesSelectors.scopeCustomMeta({
              scope: getStoreScope(ActivitiesDataScope.Requisition, requisition.id),
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(meta => {
        this.hasMore = meta?.hasMore ?? true;
      });
  }

  public openActivity(activity: TrackingActivity): void {
    this.activityFacade.openActivity(activity);
  }

  public loadMore(): void {
    if (!this.hasMore) return;
    if (this.activities?.length) {
      this.cursorPosition = this.activities[this.activities.length - 1].id;
    }
    this.loadActivities();
  }

  private loadActivities(): void {
    const requisitionActivitiesScope = getStoreScope(
      ActivitiesDataScope.Requisition,
      this.requisitionId,
    );
    this.store.dispatch(
      TrackingActivitiesActions.fetchRequisitionTrackingActivities({
        payload: {
          params: {
            requisitionId: this.requisitionId,
            filters: {
              ...DefaultActivitiesAndNotificationsFilters,
              cursorPosition: this.cursorPosition,
            },
          },
          scope: requisitionActivitiesScope,
          replace: false,
        },
      }),
    );
  }
}
